import React, { useEffect, useMemo, useState } from 'react';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import AddOnsPage from '../addons/AddOnsPage';
import PackagesPage from '../packages/PackagesPage';
import ServiceDepositSettings from '../../settings/deposits/ServiceDepositSettings';
import ServiceTaxSettings from '../../settings/tax/ServiceTaxSettings';
import '../styles/services.css';
import './service-details-v2.css';
import BackButton from '../../../shared/components/back-button/BackButton';
import ActionButton from '../../../shared/components/action-button/ActionButton';
import { getServicePathSegment } from '../utils/serviceSlug';
import { getService, upsertService } from '../../../api/companyRecords';

export default function ServiceDetailsV2({ serviceId, onBack }) {
  const navigate = useNavigate();
  const { companyData } = useAuth();
  const muiTheme = useMuiTheme?.();
  const isDark = muiTheme?.palette?.mode === 'dark';
  const [activeTab, setActiveTab] = useState(0);
  const [currentService, setCurrentService] = useState(null);
  const [showTabInfo, setShowTabInfo] = useState(true);
  const { setNotification } = useNotification();

  // Allow deep-linking into a specific tab via `?tab=packages|addons|deposits|tax`
  useEffect(() => {
    try {
      const tab = new URLSearchParams(window.location.search).get('tab');
      if (!tab) return;
      const map = { addons: 0, packages: 1, deposits: 2, tax: 3 };
      const next = map[String(tab).toLowerCase()];
      if (typeof next === 'number') setActiveTab(next);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!serviceId || !companyData?.id) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const service = await getService({ companyId: companyData.id, id: serviceId });
        if (!cancelled && service) setCurrentService(service);
      } catch (error) {
        console.error('Error loading service:', error);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [serviceId, companyData?.id]);

  const formatPrice = (price) => Number(price || 0).toFixed(2);

  const getTabInfo = (tabIndex) => {
    switch (tabIndex) {
      case 0:
        return {
          title: 'Add-ons Management',
          description:
            'Create and manage additional services clients can add to this base service. Great for upsells and customization.',
        };
      case 1:
        return {
          title: 'Service Packages',
          description: 'Bundle this service with others to create attractive packages and special deals.',
        };
      case 2:
        return {
          title: 'Deposit Settings',
          description: 'Override global deposit settings if this service needs different deposit rules.',
        };
      case 3:
        return {
          title: 'Tax Settings',
          description: 'Configure service-specific tax rates for both service price and deposits.',
        };
      default:
        return null;
    }
  };

  const currentTabInfo = useMemo(() => getTabInfo(activeTab), [activeTab]);

  const cssVars = useMemo(() => {
    // Ensure consistent tokens across app vs global [data-theme] side-effects.
    if (isDark) {
      return {
        '--bg-0': '#1a1d24',
        '--bg-50': '#1a1d24',
        '--bg-paper': '#1a1d24',
        '--text-900': '#e5e7eb',
        '--text-700': '#e5e7eb',
        '--text-500': '#9ca3af',
        '--text-primary': '#e5e7eb',
        '--text-secondary': '#9ca3af',
        '--border-200': 'rgba(255,255,255,0.12)',
        '--border-300': 'rgba(255,255,255,0.18)',
        '--border-primary': 'rgba(255,255,255,0.12)',
      };
    }
    return {
      '--bg-0': '#ffffff',
      '--bg-50': '#f8fafc',
      '--bg-paper': '#ffffff',
      '--text-900': '#0f172a',
      '--text-700': '#334155',
      '--text-500': '#64748b',
      '--text-primary': '#0f172a',
      '--text-secondary': '#64748b',
      '--border-200': '#e5e7eb',
      '--border-300': '#d1d5db',
      '--border-primary': '#e5e7eb',
    };
  }, [isDark]);

  const handleBack = () => {
    if (onBack) return onBack();
    // Default: return to Settings -> Services & Categories -> Services (desktop settings listens to this navigation state)
    navigate('/app/overview/settings', {
      state: { section: 'services-categories', parent: 'services-categories', servicesTab: 'services' },
    });
  };

  const handleSaveDepositSettings = async (depositSettings) => {
    if (!serviceId || !companyData?.id) return;
    try {
      await upsertService({
        companyId: companyData.id,
        id: serviceId,
        service: { depositSettings, updatedAt: new Date().toISOString() },
      });
      setNotification({ open: true, message: 'Deposit settings saved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error saving deposit settings:', error);
      setNotification({ open: true, message: `Error saving deposit settings: ${error.message}`, severity: 'error' });
    }
  };

  const handleSaveTaxSettings = async (taxSettings) => {
    if (!serviceId || !companyData?.id) return;
    try {
      await upsertService({
        companyId: companyData.id,
        id: serviceId,
        service: { taxSettings, updatedAt: new Date().toISOString() },
      });
      setNotification({ open: true, message: 'Tax settings saved successfully', severity: 'success' });
    } catch (error) {
      console.error('Error saving tax settings:', error);
      setNotification({ open: true, message: `Error saving tax settings: ${error.message}`, severity: 'error' });
    }
  };

  if (!currentService) return null;

  const servicePath = getServicePathSegment(currentService);

  return (
    <div className={`svc-page sd2-page ${isDark ? 'is-dark' : ''}`} style={cssVars}>
      <div className="sd2-header">
        <div className="sd2-headerTop">
          <BackButton
            label="Back to Services"
            className="back-btn--flush"
            onClick={handleBack}
          />
          <div className="sd2-headerActions">
            <ActionButton
              variant="primary"
              onClick={() => navigate(`/app/overview/services/${servicePath}/edit`)}
              type="button"
            >
              Edit Service
            </ActionButton>
          </div>
        </div>

        <div className="sd2-headerMain">
          <p className="sd2-pageDesc">Manage add-ons, packages, deposits, and tax settings for this service.</p>
        </div>
      </div>

      <div className="sd2-summary">
        <div className="sd2-summaryCard">
          <div className="sd2-summaryTop">
            <div className="sd2-thumb" aria-hidden>
              {currentService.imageUrl ? <img src={currentService.imageUrl} alt="" /> : <div className="sd2-thumbFallback">🏷️</div>}
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="sd2-serviceName" title={currentService.name}>
                {currentService.name}
              </div>
              {currentService.description ? (
                <p className="sd2-serviceDescription">{currentService.description}</p>
              ) : (
                <div className="sd2-muted">Clients see this in your booking flow.</div>
              )}
            </div>
          </div>

          <div className="sd2-statGrid">
            <div className="sd2-stat">
              <div className="k">Duration</div>
              <div className="v">{currentService.duration || 0} min</div>
            </div>
            <div className="sd2-stat">
              <div className="k">Price</div>
              <div className="v">${formatPrice(currentService.price)}</div>
            </div>
            <div className="sd2-stat">
              <div className="k">Add-ons</div>
              <div className="v">{currentService.addOns?.length || 0}</div>
            </div>
            <div className="sd2-stat">
              <div className="k">Packages</div>
              <div className="v">{currentService.packages?.length || 0}</div>
            </div>
            <div className="sd2-stat">
              <div className="k">Assigned Staff</div>
              <div className="v">{currentService.employeeIds?.length || 0}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="sd2-tabsCard">
        <div className="sd2-tabBar" role="tablist" aria-label="Service settings tabs">
          <button
            type="button"
            className={`sd2-tabBtn ${activeTab === 0 ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(0);
              setShowTabInfo(true);
            }}
            role="tab"
            aria-selected={activeTab === 0}
          >
            Add-ons
          </button>
          <button
            type="button"
            className={`sd2-tabBtn ${activeTab === 1 ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(1);
              setShowTabInfo(true);
            }}
            role="tab"
            aria-selected={activeTab === 1}
          >
            Packages
          </button>
          <button
            type="button"
            className={`sd2-tabBtn ${activeTab === 2 ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(2);
              setShowTabInfo(true);
            }}
            role="tab"
            aria-selected={activeTab === 2}
          >
            Deposits
          </button>
          <button
            type="button"
            className={`sd2-tabBtn ${activeTab === 3 ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(3);
              setShowTabInfo(true);
            }}
            role="tab"
            aria-selected={activeTab === 3}
          >
            Tax Rates
          </button>
        </div>

        {showTabInfo && currentTabInfo ? (
          <div className="sd2-callout" role="note">
            <div style={{ minWidth: 0 }}>
              <div className="sd2-calloutTitle">{currentTabInfo.title}</div>
              <div className="sd2-calloutDesc">{currentTabInfo.description}</div>
            </div>
            <button className="sd2-calloutBtn" type="button" onClick={() => setShowTabInfo(false)}>
              Dismiss
            </button>
          </div>
        ) : null}

        <div className="sd2-tabBody" role="tabpanel">
          {activeTab === 0 ? <AddOnsPage service={currentService} /> : null}
          {activeTab === 1 ? <PackagesPage service={currentService} /> : null}
          {activeTab === 2 ? <ServiceDepositSettings service={currentService} onSave={handleSaveDepositSettings} /> : null}
          {activeTab === 3 ? <ServiceTaxSettings service={currentService} onSave={handleSaveTaxSettings} /> : null}
        </div>
      </div>

    </div>
  );
}

